import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlayContext, useStateInRef } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { DraftingPane } from '~/components/ai/DraftingPane/DraftingPane';
import { resolveAgenticErrorMessage } from '~/components/ai/shared/resolveAgenticErrorMessage';
import { SparkleIcon } from '~/components/ai/shared/SparkleIcon/SparkleIcon';
import { useFileAttachment } from '~/components/ai/shared/useFileAttachment';
import { useIoContext, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { activeConfirmation, mergeLocationState, readConversationIdFromLocationState } from '../SingleDraftAgentPane/SingleDraftAgentPane.utils';
import { describeError, findLatestPersistedFilterUpdate } from './BulkDraftAgentPane.utils';
export const GetBulkDraftAgentPaneTID = (parentTestId)=>getTestIds(parentTestId);
// WHY: sits below the scroll-independent sticky bulk-actions bar
const PANE_TOP_PX = 192;
const SCREEN_BOTTOM_GAP_PX = 32;
const SCREEN_RIGHT_GAP_PX = 24;
const PANE_WIDTH_PX = 482;
const BULK_INTERCOM_VERTICAL_PADDING = 100;
const initialChatState = {
    messages: [],
    isThinking: false,
    isUploading: false,
    uploadingFileName: null,
    pendingAttachment: null,
    activeToolCalls: [],
    thinkingText: null,
    thinkingLabel: null,
    error: null,
    undoPendingAppliedId: null,
    localDecisions: {}
};
export const BulkDraftAgentPane = (param)=>{
    let { scope, draftNames, onFilterUpdate, onDraftsChanged, onAssistantMessage, isOpen: controlledIsOpen, onOpenChange, testId = '' } = param;
    const { t } = useLocaleKeys();
    const location = useLocation();
    const navigate = useNavigate();
    const restoredConversationId = readConversationIdFromLocationState(location.state);
    const { BulkDraftAgentPaneTestIds } = useMemo(()=>GetBulkDraftAgentPaneTID(testId), [
        testId
    ]);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = controlledIsOpen != null;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (open)=>{
        if (!isControlled) setInternalIsOpen(open);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    };
    const [chat, setChat] = useState(initialChatState);
    const { api: storeApi } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const { getNow } = useIoContext();
    const { sendFileUpload, removePendingAttachment } = useFileAttachment(setChat);
    const conversationIdRef = useRef(restoredConversationId);
    const createInFlightRef = useRef(null);
    const abortRef = useRef(null);
    const turnInFlightRef = useRef(false);
    const activeMessage = activeConfirmation(chat.messages, chat.localDecisions);
    const hasUserMessage = chat.messages.some((m)=>m.role === 'user');
    const isClearDisabled = !hasUserMessage || chat.thinkingLabel != null;
    const panePosition = {
        top: 'auto',
        bottom: "".concat(SCREEN_BOTTOM_GAP_PX, "px"),
        right: "".concat(SCREEN_RIGHT_GAP_PX, "px"),
        width: "".concat(PANE_WIDTH_PX, "px"),
        height: "calc(100vh - ".concat(PANE_TOP_PX, "px - ").concat(SCREEN_BOTTOM_GAP_PX, "px)")
    };
    const getEnsureConversationId = useStateInRef(ensureConversationId);
    const getOnStreamError = useStateInRef(onStreamError);
    const getLocation = useStateInRef(location);
    useEffect(()=>()=>{
            var _abortRef_current;
            return (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        }, []);
    useOnMount(()=>{
        const id = conversationIdRef.current;
        if (id == null) return undefined;
        const controller = new AbortController();
        abortRef.current = controller;
        let cancelled = false;
        (async ()=>{
            try {
                const conversation = await storeApi.agenticBulkDraft.get(id);
                if (cancelled || controller.signal.aborted) return;
                onHydrate(conversation.messages);
            } catch (unused) {
                if (cancelled || controller.signal.aborted) return;
                showErrorToast(t.agenticDrafting.errors.hydrate());
            }
        })();
        return ()=>{
            cancelled = true;
            controller.abort();
        };
    });
    useEffect(()=>{
        const shouldStartConversation = isOpen && conversationIdRef.current == null && createInFlightRef.current == null;
        if (shouldStartConversation) {
            getEnsureConversationId()().catch((err)=>getOnStreamError()(describeError(err)));
        }
    }, [
        isOpen
    ]);
    function persistConversationId(conversationId) {
        const loc = getLocation();
        navigate("".concat(loc.pathname).concat(loc.search), {
            replace: true,
            state: mergeLocationState(loc.state, {
                conversationId
            })
        });
    }
    function ensureConversationId() {
        if (conversationIdRef.current != null) return Promise.resolve(conversationIdRef.current);
        if (createInFlightRef.current != null) return createInFlightRef.current;
        const pending = storeApi.agenticBulkDraft.create({
            matchedDraftCount: scope.matchedDraftCount,
            draftNames
        }).then((param)=>{
            let { conversationId, message } = param;
            conversationIdRef.current = conversationId;
            persistConversationId(conversationId);
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    messages: [
                        ...prev.messages,
                        message
                    ]
                }));
            return conversationId;
        }).finally(()=>{
            createInFlightRef.current = null;
        });
        createInFlightRef.current = pending;
        return pending;
    }
    const api = {
        ensureId: ensureConversationId,
        get: (conversationId)=>storeApi.agenticBulkDraft.get(conversationId),
        sendMessage: (args)=>storeApi.agenticBulkDraft.sendMessage(_object_spread_props(_object_spread({}, args), {
                scope
            })),
        undo: (args)=>storeApi.agenticBulkDraft.undo(args)
    };
    function onHydrate(messages) {
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                messages,
                localDecisions: {}
            }));
        const filterUpdate = findLatestPersistedFilterUpdate(messages);
        if (filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(filterUpdate);
    }
    function onStreamError(error) {
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                error,
                isThinking: false,
                activeToolCalls: [],
                thinkingText: null
            }));
    }
    function onStreamDone() {
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: false,
                activeToolCalls: [],
                thinkingText: null
            }));
    }
    const handleStreamEvent = (event)=>event.match({
            toolCallStart: (tool, args)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        activeToolCalls: [
                            ...prev.activeToolCalls,
                            {
                                tool,
                                args,
                                startedAt: getNow().getTime()
                            }
                        ]
                    })),
            toolCallEnd: (tool, durationMs)=>setChat((prev)=>{
                    const idx = prev.activeToolCalls.findIndex((tc)=>tc.tool === tool && tc.durationMs == null);
                    if (idx === -1) return prev;
                    const next = [
                        ...prev.activeToolCalls
                    ];
                    next[idx] = _object_spread_props(_object_spread({}, next[idx]), {
                        durationMs
                    });
                    return _object_spread_props(_object_spread({}, prev), {
                        activeToolCalls: next
                    });
                }),
            thinking: (text)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        thinkingText: text
                    })),
            ack: (text)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        thinkingText: text
                    })),
            assistantMessage: (message)=>{
                if ((message.content.type === 'bulk-confirmation' || message.content.type === 'chat-text') && message.content.filterUpdate != null) {
                    onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(message.content.filterUpdate);
                }
                if (message.content.type === 'bulk-applied') {
                    onDraftsChanged === null || onDraftsChanged === void 0 ? void 0 : onDraftsChanged(message.content.appliedIds);
                } else if (message.content.type === 'reverted') {
                    onDraftsChanged === null || onDraftsChanged === void 0 ? void 0 : onDraftsChanged(message.content.revertedItemIds);
                }
                onAssistantMessage === null || onAssistantMessage === void 0 ? void 0 : onAssistantMessage(message);
                setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        messages: [
                            ...prev.messages,
                            message
                        ],
                        activeToolCalls: [],
                        thinkingText: null,
                        isThinking: false
                    }));
            },
            streamError: (errorCode)=>onStreamError(resolveAgenticErrorMessage(t, errorCode)),
            draftCreated: ()=>undefined,
            messageEnrichment: (messageId, contentPatch)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        messages: prev.messages.map((m)=>m.id === messageId && m.role === 'assistant' && m.content.type === 'bulk-applied' ? _object_spread_props(_object_spread({}, m), {
                                content: _object_spread({}, m.content, contentPatch)
                            }) : m)
                    })),
            reset: ()=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        isThinking: false,
                        activeToolCalls: [],
                        thinkingText: null
                    }))
        });
    const runTurn = async (content)=>{
        if (turnInFlightRef.current) return;
        turnInFlightRef.current = true;
        const controller = new AbortController();
        abortRef.current = controller;
        const userMessageId = generateUniqueId('msg');
        const timestamp = getNow().toISOString();
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                messages: [
                    ...prev.messages,
                    {
                        id: userMessageId,
                        role: 'user',
                        timestamp,
                        content
                    }
                ],
                isThinking: true,
                error: null
            }));
        try {
            let id;
            try {
                id = await ensureConversationId();
            } catch (err) {
                if (!controller.signal.aborted) onStreamError(describeError(err));
                return;
            }
            try {
                await api.sendMessage({
                    conversationId: id,
                    content,
                    signal: controller.signal,
                    onEvent: handleStreamEvent
                });
                onStreamDone();
            } catch (err) {
                if (!controller.signal.aborted) onStreamError(describeError(err));
            }
        } finally{
            turnInFlightRef.current = false;
        }
    };
    const onClearChat = async ()=>{
        var _abortRef_current;
        (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        conversationIdRef.current = null;
        turnInFlightRef.current = false;
        createInFlightRef.current = null;
        setChat(_object_spread_props(_object_spread({}, initialChatState), {
            isThinking: true,
            thinkingLabel: t.agenticDrafting.body.clearing()
        }));
        const result = await handleRequest(ensureConversationId());
        if (!result.success) showErrorToast(t.agenticDrafting.errors.clear());
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: false,
                thinkingLabel: null
            }));
    };
    const sendPrompt = (text, options)=>{
        const attachment = chat.pendingAttachment;
        if (attachment != null) {
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    pendingAttachment: null
                }));
            const trimmed = text.trim();
            return runTurn(_object_spread({
                type: 'file-uploaded',
                fileId: attachment.fileId,
                fileName: attachment.fileName
            }, trimmed.length > 0 ? {
                text: trimmed
            } : {}));
        }
        return runTurn(_object_spread({
            type: 'prompt',
            text
        }, (options === null || options === void 0 ? void 0 : options.intentHint) != null ? {
            intentHint: options.intentHint
        } : {}));
    };
    const dismissProposal = (messageId)=>runTurn({
            type: 'dismiss',
            messageId
        });
    const skipProposal = (messageId)=>runTurn({
            type: 'skip',
            messageId
        });
    const skipTheRestProposal = (messageId)=>runTurn({
            type: 'skip-the-rest',
            messageId
        });
    const applyProposal = (messageId, selectedItemIds)=>runTurn({
            type: 'apply',
            messageId,
            selectedItemIds
        });
    const takeSuggestion = (messageId, suggestion)=>{
        const target = chat.messages.find((message)=>message.id === messageId);
        if ((target === null || target === void 0 ? void 0 : target.role) === 'assistant' && target.content.type === 'clarifying-question') {
            return runTurn({
                type: 'prompt',
                text: suggestion.label
            });
        }
        return runTurn({
            type: 'list-select-click',
            messageId,
            itemId: suggestion.id,
            label: suggestion.label
        });
    };
    const refreshConversation = async (conversationId)=>{
        const result = await handleRequest(api.get(conversationId));
        if (result.success) onHydrate(result.data.messages);
    };
    const undoTurn = async (messageId)=>{
        if (turnInFlightRef.current) return;
        const conversationId = conversationIdRef.current;
        if (conversationId == null) return;
        turnInFlightRef.current = true;
        const controller = new AbortController();
        abortRef.current = controller;
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                undoPendingAppliedId: messageId,
                isThinking: true,
                error: null
            }));
        try {
            await api.undo({
                conversationId,
                messageId,
                signal: controller.signal,
                onEvent: handleStreamEvent
            });
            onStreamDone();
        } catch (err) {
            if (controller.signal.aborted) return;
            const status = err.status;
            if (status === 400 || status === 410) {
                onStreamDone();
                await refreshConversation(conversationId);
            } else {
                onStreamError(describeError(err));
            }
        } finally{
            turnInFlightRef.current = false;
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    undoPendingAppliedId: null,
                    isThinking: false,
                    activeToolCalls: [],
                    thinkingText: null
                }));
        }
    };
    return /*#__PURE__*/ _jsx(DraftingPane, {
        testId: BulkDraftAgentPaneTestIds.OpenPaneHeader(),
        isOpen: isOpen,
        position: panePosition,
        dragResize: true,
        intercom: {
            collapsedVerticalPadding: BULK_INTERCOM_VERTICAL_PADDING
        },
        header: {
            mode: 'collapsible',
            icon: /*#__PURE__*/ _jsx(SparkleIcon, {}),
            title: t.agenticDrafting.header.bulkTitle(),
            onClearChat,
            isClearDisabled,
            clearChatBiName: 'clear_bulk_agent_chat',
            onToggle: ()=>setIsOpen(!isOpen),
            onExpand: ()=>setIsOpen(true),
            toggleBiName: 'toggle_bulk_agent_pane',
            collapseChevronOrientation: 'vertical'
        },
        chat: chat,
        activeMessage: activeMessage,
        placeholderPool: [],
        onSubmitPrompt: sendPrompt,
        onFileUpload: sendFileUpload,
        onRemoveAttachment: removePendingAttachment,
        onDismissProposal: dismissProposal,
        onApplyProposal: applyProposal,
        onSkipProposal: skipProposal,
        onSkipTheRestProposals: skipTheRestProposal,
        onListSelectClick: (messageId, itemId)=>applyProposal(messageId, [
                itemId
            ]),
        onNumberedListClick: (messageId, option)=>applyProposal(messageId, [
                option.id
            ]),
        onPickFollowUpSuggestion: takeSuggestion,
        onSubmitFeedback: ()=>undefined,
        onDismissFeedback: ()=>undefined,
        onUndoProposal: undoTurn
    });
};
